/*
  Departure-board split-flap animation — the ONE driver for every flap-tile on the board design.
  Loaded (deferred) by layout/theme.liquid only when snippets/board-flag.liquid is true; styles are
  in assets/board.css ("9 split-flap animation").

  Markup contract (server-rendered, final characters already in place):
    [data-flap]                a group, animated once, the first time it scrolls into view
      [data-flap-tile]         a split-flap TILE holding ONE character (aria-hidden markup): a 3D
                               flip, top flap falling around the hinge, then the new bottom half
      [data-flap-text]         a run of text (a 3-letter code, a price): letters cycle through
                               letters, digits through digits; anything else stays put as plain
                               text. data-flap-text="digits" cycles digits only (a price).
    data-flap-order="rows"     on the group: each [data-flap-row] is observed and animated on its
                               own, when IT scrolls into view (a long board never animates rows
                               below the fold); at most MAX_ROWS rows animate at once, and a row
                               still waiting when it has already left the viewport is skipped.

  Progressive enhancement, never a dependency:
  - The real characters are in the HTML from the first paint (LCP, SEO, accessible names). An
    animation only draws aria-hidden overlay layers over them and makes the original glyph
    transparent while it runs; the overlays are removed at the end. Nothing changes size.
  - No JS, no IntersectionObserver / Element.animate, or prefers-reduced-motion: reduce (checked
    before every run, and switching it on mid-animation snaps everything to its final state):
    static tiles.
  - Starts after the window load event, so it never competes with the LCP render.
  - Timing runs on ONE requestAnimationFrame scheduler shared by everything (no per-character
    timers). Stagger is cumulative (fixed step + bounded jitter), so items always start in reading
    order, which is DOM order, also in RTL.
  - ONE IntersectionObserver; the theme editor's section load/unload events add or cancel a
    section's work.
*/
(function () {
  if (window.__boardFlap) return;
  window.__boardFlap = true;
  if (!('IntersectionObserver' in window) || !Element.prototype.animate) return;

  var LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  var DIGITS = '0123456789';
  var TILE_POOL = LETTERS + DIGITS + '?!/.-';
  var MAX_ROWS = 8;
  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var reduced = function () { return !!(mq && mq.matches); };
  var rand = function (min, max) { return min + Math.random() * (max - min); };
  var pick = function (pool, not) {
    var c = pool.charAt(Math.floor(Math.random() * pool.length));
    return c === not && pool.length > 1 ? pick(pool, not) : c;
  };
  var poolFor = function (ch, digitsOnly) {
    if (DIGITS.indexOf(ch) !== -1) return DIGITS;
    if (!digitsOnly && LETTERS.indexOf(ch.toUpperCase()) !== -1 && ch.toUpperCase() !== ch.toLowerCase()) return LETTERS;
    return '';
  };
  var span = function (cls, text) {
    var s = document.createElement('span');
    s.className = cls;
    if (text != null) s.textContent = text;
    return s;
  };

  // ---------------------------------------------------------------- shared scheduler
  // One rAF loop over a time-ordered queue; every job belongs to a "unit" (a group or a row) so a
  // unit's pending work can be cancelled as a whole.
  var queue = [];
  var rafId = 0;
  var now = function () { return window.performance ? performance.now() : Date.now(); };
  var loop = function () {
    rafId = 0;
    var t = now();
    var due = [];
    queue = queue.filter(function (job) { if (job.at <= t) { due.push(job); return false; } return true; });
    due.sort(function (a, b) { return a.at - b.at; });
    due.forEach(function (job) { job.fn(); });
    if (queue.length) rafId = requestAnimationFrame(loop);
  };
  var later = function (unit, ms, fn) {
    queue.push({ at: now() + ms, fn: fn, unit: unit });
    if (!rafId) rafId = requestAnimationFrame(loop);
  };

  // ---------------------------------------------------------------- per-unit bookkeeping
  var active = new Map(); // unit -> { layers: [], nodes: [], anims: [], pending: n, done: fn }
  var record = function (unit) {
    var r = active.get(unit);
    if (!r) { r = { layers: [], nodes: [], anims: [], pending: 0 }; active.set(unit, r); }
    return r;
  };
  var finishItem = function (unit) {
    var r = active.get(unit);
    if (!r) return;
    r.pending--;
    if (r.pending <= 0) { active.delete(unit); unitDone(unit); }
  };
  // Snap a unit to its final state at once: no layers, no transparent glyphs, no pending jobs.
  var cancelUnit = function (unit) {
    queue = queue.filter(function (job) { return job.unit !== unit; });
    var r = active.get(unit);
    if (!r) return;
    r.anims.forEach(function (a) { try { a.cancel(); } catch (e) {} });
    r.layers.forEach(function (l) { if (l.parentNode) l.parentNode.removeChild(l); });
    r.nodes.forEach(function (n) { n.classList.remove('bf-run', 'bf-run-text'); });
    active.delete(unit);
    unitDone(unit);
  };

  // ---------------------------------------------------------------- one split-flap tile
  // Layers (aria-hidden): static top half = NEXT char, static bottom half = CURRENT char, falling
  // top flap = CURRENT char (rotateX 0 -> -90 around the hinge), rising bottom flap = NEXT char
  // (rotateX 90 -> 0). Only transform and opacity animate.
  function half(where, ch) {
    var h = span('bf-h bf-h--' + where);
    h.appendChild(span('bf-g', ch));
    return h;
  }
  function flipTile(unit, tile, delay) {
    var r = record(unit);
    r.pending++;
    later(unit, delay, function () {
      var finalChar = tile.textContent.trim().charAt(0);
      var cs = getComputedStyle(tile);
      var box = span('bf');
      box.setAttribute('aria-hidden', 'true');
      box.style.color = cs.color;
      box.style.setProperty('--bf-bg', cs.backgroundColor);
      var topNext = half('top', finalChar);
      var botCur = half('bot', finalChar);
      var flapTop = half('top', finalChar);
      flapTop.classList.add('bf-flap', 'bf-flap--top');
      flapTop.appendChild(span('bf-shade'));
      var flapBot = half('bot', finalChar);
      flapBot.classList.add('bf-flap', 'bf-flap--bot');
      flapBot.appendChild(span('bf-shade'));
      box.appendChild(topNext); box.appendChild(botCur); box.appendChild(flapTop); box.appendChild(flapBot);
      tile.appendChild(box);
      tile.classList.add('bf-run');
      r.layers.push(box); r.nodes.push(tile);

      var flips = Math.round(rand(6, 12));
      var flipMs = rand(60, 80);
      var seq = [];
      for (var i = 0; i < flips - 1; i++) seq.push(pick(TILE_POOL, seq[i - 1] || finalChar));
      seq.push(finalChar);
      var current = finalChar;
      var set = function (layer, ch) { layer.firstChild.textContent = ch; };
      var track = function (a) { r.anims.push(a); return a; };
      var step = function (k) {
        if (!active.has(unit)) return;
        if (k >= seq.length) {
          box.remove();
          tile.classList.remove('bf-run');
          finishItem(unit);
          return;
        }
        var next = seq[k];
        var ms = flipMs * rand(0.85, 1.15);
        set(topNext, next); set(botCur, current); set(flapTop, current); set(flapBot, next);
        var a = track(flapTop.animate([{ transform: 'rotateX(0deg)' }, { transform: 'rotateX(-90deg)' }], { duration: ms / 2, easing: 'ease-in', fill: 'forwards' }));
        var as = track(flapTop.lastChild.animate([{ opacity: 0 }, { opacity: 1 }], { duration: ms / 2, easing: 'ease-in', fill: 'forwards' }));
        a.onfinish = function () {
          if (!active.has(unit)) return;
          var b = track(flapBot.animate([{ transform: 'rotateX(90deg)' }, { transform: 'rotateX(0deg)' }], { duration: ms / 2, easing: 'ease-out', fill: 'forwards' }));
          var bs = track(flapBot.lastChild.animate([{ opacity: 1 }, { opacity: 0 }], { duration: ms / 2, easing: 'ease-out', fill: 'forwards' }));
          b.onfinish = function () {
            if (!active.has(unit)) return;
            current = next;
            [a, as, b, bs].forEach(function (x) { x.cancel(); r.anims.splice(r.anims.indexOf(x), 1); });
            set(flapTop, current); set(botCur, current); set(topNext, current);
            step(k + 1);
          };
        };
      };
      step(0);
    });
  }

  // ---------------------------------------------------------------- a run of text
  // An aria-hidden overlay laid exactly over the real text. Only characters that animate
  // (digits; Latin letters unless digits-only) get their own inline-block span; everything else
  // stays contiguous plain text, so Arabic and other scripts keep their shaping, and the overlay
  // takes the element's own direction.
  function flipText(unit, node, delay, steps, stepMs) {
    var r = record(unit);
    r.pending++;
    later(unit, delay, function () {
      var text = node.textContent;
      var digitsOnly = node.getAttribute('data-flap-text') === 'digits';
      var cs = getComputedStyle(node);
      var layer = span('bf-text');
      layer.setAttribute('aria-hidden', 'true');
      layer.setAttribute('dir', cs.direction === 'rtl' ? 'rtl' : 'ltr');
      layer.style.color = cs.color;
      var cells = [];
      var plain = '';
      var flush = function () { if (plain) { layer.appendChild(document.createTextNode(plain)); plain = ''; } };
      for (var i = 0; i < text.length; i++) {
        var ch = text.charAt(i);
        var pool = poolFor(ch, digitsOnly);
        if (!pool) { plain += ch; continue; }
        flush();
        var c = span('bf-c', pick(pool, ch));
        layer.appendChild(c);
        cells.push({ span: c, ch: ch, pool: pool });
      }
      flush();
      if (!cells.length) { finishItem(unit); return; }
      node.appendChild(layer);
      node.classList.add('bf-run-text');
      r.layers.push(layer); r.nodes.push(node);
      var running = cells.length;
      cells.forEach(function (c, idx) {
        var n = Math.max(2, Math.round(steps * rand(0.7, 1.2)));
        var t = 0;
        var tick = function () {
          t++;
          var last = t >= n;
          c.span.textContent = last ? c.ch : pick(c.pool, c.span.textContent);
          r.anims.push(c.span.animate([{ transform: 'scaleY(0.15)', opacity: 0.6 }, { transform: 'scaleY(1)', opacity: 1 }], { duration: Math.min(stepMs, 70), easing: 'ease-out' }));
          if (!last) { later(unit, stepMs * rand(0.8, 1.2), tick); return; }
          running--;
          if (!running) {
            layer.remove();
            node.classList.remove('bf-run-text');
            finishItem(unit);
          }
        };
        later(unit, idx * 30 + stepMs, tick);
      });
    });
  }

  // ---------------------------------------------------------------- units, rows, observer
  var visible = new Set();    // units currently intersecting
  var rowWaiting = [];        // row units waiting for a slot, in arrival (reading) order
  var rowsRunning = 0;
  var unitKind = new Map();   // unit -> 'group' | 'row'
  var unitDone = function (unit) {
    if (unitKind.get(unit) === 'row') { rowsRunning = Math.max(0, rowsRunning - 1); pumpRows(); }
  };
  var itemsOf = function (unit) {
    return Array.prototype.slice.call(unit.querySelectorAll('[data-flap-tile], [data-flap-text]')).filter(function (n) {
      return n.offsetParent !== null && n.textContent.trim() !== '';
    });
  };
  // Cumulative stagger: each item starts one step (plus bounded jitter) after the previous one,
  // so the order is always the reading order.
  var runUnit = function (unit, baseDelay) {
    if (reduced()) { unitDone(unit); return; }
    var items = itemsOf(unit);
    if (!items.length) { unitDone(unit); return; }
    var rowMode = unitKind.get(unit) === 'row';
    var delay = baseDelay || 0;
    items.forEach(function (node, i) {
      if (i > 0) delay += rowMode ? 25 + rand(0, 10) : 45 + rand(0, 20);
      if (node.hasAttribute('data-flap-tile')) flipTile(unit, node, delay);
      else flipText(unit, node, delay, rowMode ? 5 : 7, 55);
    });
  };
  // Rows that start in one burst are offset cumulatively (top to bottom); a row starting on its
  // own (scrolled to later) starts at once.
  var rowGap = 0;
  var lastRowStart = -1e9;
  var pumpRows = function () {
    while (rowsRunning < MAX_ROWS && rowWaiting.length) {
      var row = rowWaiting.shift();
      if (!visible.has(row) || !row.isConnected) continue; // scrolled past while waiting: stays static
      var t = now();
      if (t - lastRowStart > 400) rowGap = 0;
      lastRowStart = t;
      rowsRunning++;
      runUnit(row, rowGap);
      rowGap += 60 + rand(0, 15);
    }
  };
  var io = new IntersectionObserver(function (entries) {
    // Entries arrive in DOM order for one batch; keep that order.
    entries.forEach(function (entry) {
      var unit = entry.target;
      if (!entry.isIntersecting) { visible.delete(unit); return; }
      visible.add(unit);
      if (unit.__flapDone) return;
      unit.__flapDone = true;
      if (unitKind.get(unit) === 'row') { rowWaiting.push(unit); }
      else runUnit(unit, 0);
    });
    pumpRows();
  }, { threshold: 0, rootMargin: '0px 0px -12% 0px' });

  var observeIn = function (root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-flap]'), function (group) {
      if (group.__flapSeen) return;
      group.__flapSeen = true;
      if (group.getAttribute('data-flap-order') === 'rows') {
        Array.prototype.forEach.call(group.querySelectorAll('[data-flap-row]'), function (row) {
          unitKind.set(row, 'row');
          io.observe(row);
        });
      } else {
        unitKind.set(group, 'group');
        io.observe(group);
      }
    });
  };
  var forgetIn = function (root) {
    unitKind.forEach(function (kind, unit) {
      if (!root.contains(unit)) return;
      io.unobserve(unit);
      visible.delete(unit);
      rowWaiting = rowWaiting.filter(function (r) { return r !== unit; });
      cancelUnit(unit);
      unitKind.delete(unit);
    });
  };
  var snapAll = function () {
    rowWaiting = [];
    Array.from(active.keys()).forEach(cancelUnit);
    queue = [];
  };

  if (mq) {
    var onMotion = function () { if (mq.matches) snapAll(); };
    if (mq.addEventListener) mq.addEventListener('change', onMotion); else if (mq.addListener) mq.addListener(onMotion);
  }
  var start = function () { observeIn(document); };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start);
  // Theme editor: a re-rendered section gets its animation; an unloaded one loses all its work.
  document.addEventListener('shopify:section:load', function (e) { if (e.target) observeIn(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { if (e.target) forgetIn(e.target); });
})();
